import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PoundSterling, Info, Calendar } from "lucide-react";

// Tax Year configurations
const TAX_YEARS = {
  "2024/25": {
    label: "2024/25",
    personalAllowance: 12570,
    basicRateLimit: 50270,
    higherRateLimit: 125140,
    basicRate: 0.2,
    higherRate: 0.4,
    additionalRate: 0.45,
    niLower: 12570,
    niUpper: 50270,
    niRateLower: 0.08,
    niRateUpper: 0.02,
  },
  "2025/26": {
    label: "2025/26",
    // 2025/26 rates (thresholds frozen, NI rates same)
    personalAllowance: 12570,
    basicRateLimit: 50270,
    higherRateLimit: 125140,
    basicRate: 0.2,
    higherRate: 0.4,
    additionalRate: 0.45,
    niLower: 12570,
    niUpper: 50270,
    niRateLower: 0.08,
    niRateUpper: 0.02,
  },
};

function calcTax(gross, config) {
  let tax = 0;
  const { personalAllowance, basicRateLimit, higherRateLimit, basicRate, higherRate, additionalRate } = config;
  
  // Reduce personal allowance for income over £100,000
  let adjustedPersonalAllowance = personalAllowance;
  if (gross > 100000) {
    adjustedPersonalAllowance = Math.max(0, personalAllowance - Math.floor((gross - 100000) / 2));
  }
  
  const taxable = Math.max(0, gross - adjustedPersonalAllowance);
  if (taxable <= 0) return 0;
  
  if (gross <= basicRateLimit) {
    tax = taxable * basicRate;
  } else if (gross <= higherRateLimit) {
    const basicTaxable = Math.min(taxable, basicRateLimit - adjustedPersonalAllowance);
    const higherTaxable = gross - basicRateLimit;
    tax = basicTaxable * basicRate + higherTaxable * higherRate;
  } else {
    const basicTaxable = basicRateLimit - adjustedPersonalAllowance;
    const higherTaxable = higherRateLimit - basicRateLimit;
    const additionalTaxable = gross - higherRateLimit;
    tax = basicTaxable * basicRate + higherTaxable * higherRate + additionalTaxable * additionalRate;
  }
  return Math.max(0, tax);
}

function calcNI(gross, config) {
  const { niLower, niUpper, niRateLower, niRateUpper } = config;
  if (gross <= niLower) return 0;
  if (gross <= niUpper) {
    return (gross - niLower) * niRateLower;
  }
  return (niUpper - niLower) * niRateLower + (gross - niUpper) * niRateUpper;
}

export default function SalaryCalculator() {
  const [salary, setSalary] = useState(35000);
  const [pensionPct, setPensionPct] = useState(5);
  const [taxYear, setTaxYear] = useState("2024/25");

  const config = TAX_YEARS[taxYear];
  
  const pensionAmount = salary * (pensionPct / 100);
  const taxableAfterPension = salary - pensionAmount;
  const incomeTax = calcTax(taxableAfterPension, config);
  const nationalInsurance = calcNI(taxableAfterPension, config);
  const takeHome = salary - incomeTax - nationalInsurance - pensionAmount;

  const fmt = (n) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

  const rows = [
    { label: "Gross Salary", value: fmt(salary), cls: "text-white" },
    { label: `Pension (${pensionPct}%)`, value: `- ${fmt(pensionAmount)}`, cls: "text-slate-400" },
    { label: "Income Tax", value: `- ${fmt(incomeTax)}`, cls: "text-red-400" },
    { label: "National Insurance", value: `- ${fmt(nationalInsurance)}`, cls: "text-orange-400" },
    { label: "Take Home (Net)", value: fmt(takeHome), cls: "text-emerald-400 font-bold text-lg" },
  ];

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
            <PoundSterling className="w-5 h-5 text-sky-400" />
          </div>
          <span className="text-sky-400 text-sm font-medium">Salary</span>
        </div>
        <h1
          data-testid="salary-calculator-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          UK Take Home Pay
        </h1>
        <p className="text-slate-400 mb-10">
          Calculate your net salary after Income Tax and National Insurance. Select your tax year below.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div data-testid="salary-inputs" className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
            <h2 className="font-outfit font-semibold text-white text-lg">Your Details</h2>

            {/* Tax Year Toggle */}
            <div>
              <label className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                <Calendar className="w-4 h-4" />
                Tax Year
              </label>
              <div data-testid="tax-year-toggle" className="flex rounded-xl overflow-hidden border border-slate-700">
                {Object.keys(TAX_YEARS).map((year) => (
                  <button
                    key={year}
                    data-testid={`tax-year-${year.replace("/", "-")}`}
                    onClick={() => setTaxYear(year)}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
                      taxYear === year
                        ? "bg-sky-500 text-slate-950"
                        : "bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800"
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
              <p className="text-slate-600 text-xs mt-2">
                {taxYear === "2025/26" 
                  ? "Tax year: 6 April 2025 – 5 April 2026" 
                  : "Tax year: 6 April 2024 – 5 April 2025"}
              </p>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">Gross Annual Salary</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                <input
                  type="number"
                  data-testid="input-salary"
                  value={salary}
                  onChange={(e) => setSalary(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-1.5">
                Pension Contribution: <span className="text-sky-400 font-medium">{pensionPct}%</span>
              </label>
              <input
                type="range"
                data-testid="input-pension"
                min="0"
                max="40"
                step="1"
                value={pensionPct}
                onChange={(e) => setPensionPct(Number(e.target.value))}
                className="w-full accent-sky-400 cursor-pointer"
              />
              <div className="flex justify-between text-slate-600 text-xs mt-1">
                <span>0%</span><span>40%</span>
              </div>
            </div>

            <div className="flex items-start gap-2 bg-slate-950/60 border border-slate-800/50 rounded-xl p-3">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                Pension contributions are made pre-tax, reducing your Income Tax liability. 
                {salary > 100000 && (
                  <span className="text-orange-400"> Note: Your personal allowance is reduced due to income over £100k.</span>
                )}
              </p>
            </div>
          </div>

          {/* Results */}
          <div data-testid="salary-results" className="space-y-4">
            <div className="bg-sky-500/5 border border-sky-500/20 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-1">
                <p className="text-slate-400 text-sm">Monthly Take Home</p>
                <span className="text-xs text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full">{taxYear}</span>
              </div>
              <p data-testid="result-monthly-takehome" className="font-outfit text-4xl font-bold text-sky-400">
                {fmt(takeHome / 12)}
              </p>
              <p className="text-slate-500 text-sm mt-1">{fmt(takeHome)} per year</p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 space-y-3">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between ${i === rows.length - 1 ? "pt-3 border-t border-slate-700" : ""}`}
                >
                  <span className="text-slate-400 text-sm">{row.label}</span>
                  <span className={`font-mono text-sm ${row.cls}`}>{row.value}</span>
                </div>
              ))}
            </div>

            {/* Effective Tax Rate */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Effective Tax Rate</span>
                <span className="text-white font-semibold">
                  {((incomeTax + nationalInsurance) / salary * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-2 mt-2 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((incomeTax + nationalInsurance) / salary * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
